import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;

  h3 {
    margin: 0 0 1rem 0;
    color: #1f2937;
    font-size: 1.125rem;
    font-weight: 600;
  }
`;

const ChartContainer = styled.div`
  position: relative;
  height: 300px;

  @media (max-width: 768px) {
    height: 250px;
  }
`;

const MetricsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const MetricCard = styled.div`
  background: ${props => props.color || '#f8fafc'};
  border-radius: 8px;
  padding: 1rem;
  text-align: center;

  .metric-value {
    font-size: 2rem;
    font-weight: bold;
    color: ${props => props.textColor || '#1f2937'};
    margin-bottom: 0.25rem;
  }

  .metric-label {
    font-size: 0.875rem;
    color: ${props => props.labelColor || '#6b7280'};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const AnalyticsCharts = ({ analyticsData, isLoading, error }) => {
  // Default chart options
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const lineOptions = {
    ...commonOptions,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    ...commonOptions,
    cutout: '60%',
  };

  if (isLoading) {
    return (
      <ChartsGrid>
        <ChartCard>
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            📊 Loading analytics data...
          </div>
        </ChartCard>
      </ChartsGrid>
    );
  }

  if (error) {
    return (
      <ChartsGrid>
        <ChartCard>
          <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
            ⚠️ Error loading analytics data: {error}
          </div>
        </ChartCard>
      </ChartsGrid>
    );
  }

  if (!analyticsData) {
    return (
      <ChartsGrid>
        <ChartCard>
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            📈 No analytics data available
          </div>
        </ChartCard>
      </ChartsGrid>
    );
  }

  // Extract data for metrics summary
  const realtime = analyticsData.realtime;
  const pageViews = analyticsData.pageViews;
  const topPages = analyticsData.topPages;
  const trafficSources = analyticsData.trafficSources;

  // Prepare Page Views Trend Data (Line Chart)
  const pageViewsChartData = pageViews?.dailyData ? {
    labels: pageViews.dailyData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Page Views',
        data: pageViews.dailyData.map(item => item.pageViews),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
        fill: true,
      },
      {
        label: 'Users',
        data: pageViews.dailyData.map(item => item.users),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
        fill: true,
      },
    ],
  } : null;

  // Prepare Top Pages Data (Bar Chart)
  const topPagesChartData = topPages?.slice(0, 10) ? {
    labels: topPages.slice(0, 10).map(page => {
      const title = page.title || page.page;
      return title.length > 25 ? `${title.substring(0, 25)}...` : title;
    }),
    datasets: [
      {
        label: 'Page Views',
        data: topPages.slice(0, 10).map(page => page.pageViews),
        backgroundColor: [
          '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
          '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280'
        ],
        borderRadius: 4,
      },
    ],
  } : null;

  // Prepare Traffic Sources Data (Doughnut Chart)
  const trafficSourcesChartData = trafficSources?.slice(0, 8) ? {
    labels: trafficSources.slice(0, 8).map(source => source.channelGroup || source.source),
    datasets: [
      {
        data: trafficSources.slice(0, 8).map(source => source.sessions),
        backgroundColor: [
          '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
          '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'
        ],
        borderWidth: 2,
        borderColor: '#ffffff',
      },
    ],
  } : null;

  return (
    <>
      {/* Key Metrics Summary */}
      <MetricsSummary>
        <MetricCard color="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" textColor="white" labelColor="rgba(255,255,255,0.8)">
          <div className="metric-value">{realtime?.activeUsers || 0}</div>
          <div className="metric-label">Active Users</div>
        </MetricCard>
        <MetricCard color="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" textColor="white" labelColor="rgba(255,255,255,0.8)">
          <div className="metric-value">{pageViews?.totalPageViews?.toLocaleString() || 0}</div>
          <div className="metric-label">Total Page Views</div>
        </MetricCard>
        <MetricCard color="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" textColor="white" labelColor="rgba(255,255,255,0.8)">
          <div className="metric-value">{pageViews?.totalSessions?.toLocaleString() || 0}</div>
          <div className="metric-label">Sessions</div>
        </MetricCard>
        <MetricCard color="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" textColor="white" labelColor="rgba(255,255,255,0.8)">
          <div className="metric-value">{pageViews?.totalUsers?.toLocaleString() || 0}</div>
          <div className="metric-label">Total Users</div>
        </MetricCard>
      </MetricsSummary>

      {/* Analytics Charts */}
      <ChartsGrid>
        {/* Page Views Trend */}
        {pageViewsChartData && (
          <ChartCard>
            <h3>📈 Page Views & Users Trend</h3>
            <ChartContainer>
              <Line data={pageViewsChartData} options={lineOptions} />
            </ChartContainer>
          </ChartCard>
        )}

        {/* Top Pages Performance */}
        {topPagesChartData && (
          <ChartCard>
            <h3>🏆 Top Pages Performance</h3>
            <ChartContainer>
              <Bar data={topPagesChartData} options={commonOptions} />
            </ChartContainer>
          </ChartCard>
        )}

        {/* Traffic Sources Distribution */}
        {trafficSourcesChartData && (
          <ChartCard>
            <h3>🎯 Traffic Sources Distribution</h3>
            <ChartContainer>
              <Doughnut data={trafficSourcesChartData} options={doughnutOptions} />
            </ChartContainer>
          </ChartCard>
        )}
      </ChartsGrid>
    </>
  );
};

export default AnalyticsCharts;